import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { fromEvent, Subscription, map, switchMap, of, catchError } from 'rxjs';
import { GridReadyEvent, ICellRendererParams, ViewportChangedEvent } from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { FieldExpression, LuceneExpression, NodeExpression, toString as luceneToString } from 'lucene';

import { padSupplierOrderReference } from '../../../shared/utils/padding';
import { User } from '../../../shared/models/user';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { ShipmentsService } from '../../services/shipments.service';
import { CommonGridOptions, clientSideAgGridModules } from '../../../shared/datacenter/grid-options';
import { UserService } from '../../../shared/services/user.service';
import { CommonColDef,
	DateColDef,
	IconRedirectColDef,
	LinkColDef,
	NumericColDef } from '../../../shared/datacenter/column-defs';
import { TranslationService } from '../../../shared/services/translation.service';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import {
	Document,
	GlobalDocument,
	DOCUMENTS_SUB_ENTITY,
	FieldOfDocument
} from '../../../commercial-documents/models/document';
import { ShipmentCard } from '../../models/shipment';
import { Batch } from '../../models/batch';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { BatchesService } from '../../services/batches.service';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';
const DOCUMENT_TYPE_ORDER_ACKNOWLEDEGEMENT = 'OA';
const DOCUMENT_TYPE_DISPATCH_ADVICE = 'DA';
const DOCUMENT_TYPE_INVOICE = 'IN';
const DOCUMENT_TYPE_CERTIFICATE = 'CE';

const resizeObservable = fromEvent(globalThis, 'resize');

const linkToBatchCardCellRendererParams = {
	routerLink(params: ICellRendererParams<Batch, unknown>) {
		const batch = params.data;
		if (!batch) {
			return undefined;
		}
		return {
			commands: [
				'/order-tracking',
				'batch',
				'card',
				batch.id
			]
		};
	}
};

const linkToDocumentCellRendererParams = {
	routerLink(params: ICellRendererParams<Document, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const document = params.data;
		let documentType;
		switch (document.type) {
		case 'IN':
			documentType = 'invoice-card';
			break;
		case 'CE':
			documentType = 'certificate-card';
			break;
		case 'DA':
			documentType = 'dispatch-advice-card';
			break;
		case 'OA':
			documentType = 'order-acknowledgment-card';
			break;
		default:
			throw new Error('unknown document type');
		}

		return {
			commands: [
				'/commercial-documents',
				'cards',
				documentType,
				document.id
			],
		};
	}
};

@Component({
	selector: 'copo-shipment-card',
	templateUrl: './shipment-card.component.html',
	styleUrl: './shipment-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		BackButtonComponent,
		TranslateModule,
		AfterViewInitDirective,
		MatExpansionModule,
		MatDividerModule,
		MatIconModule,
		AgGridModule,
		OfflineComponent,
		LoadingSpinnerComponent
	]
})
export class ShipmentCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	readonly UNDERSCORE = UNDERSCORE;

	user: User;
	isLoading = false;
	isDocumentLoading = false;
	isOffline = false;
	batch?: Batch;
	shipment?: ShipmentCard;
	batches?: PaginatedResult<Batch>;
	shipmentId: string;

	invoiceDisplayed = false;
	certificateDisplayed = false;
	dispatchAdviceDisplayed = false;
	orderAckDisplayed = false;
	isShipmentNotFound = false;
	documentBoxDisplayed = true;
	overlayNoRowsTemplate?: string;
	overlayNoDocumentsTemplate?: string;
	documentResults: PaginatedResult<Document> | undefined;
	shipmentBatchesOptions?: CommonGridOptions<Batch>;
	gridCommercialDocumentsOptions?: CommonGridOptions<GlobalDocument>;

	shipmentSub: Subscription;
	documentSub?: Subscription;
	resizeSub?: Subscription;
	supplierOrderNumbers: string[] = [];
	globalDocuments: GlobalDocument[] = [];

	documentsFields: readonly FieldOfDocument[] = [
		'id',
		'subtype',
		'type',
		'date',
		'name',
		'millCode',
		'proforma',
		`${ DOCUMENTS_SUB_ENTITY }.vendorBatchId`,
		`${ DOCUMENTS_SUB_ENTITY }.millCode`
	];

	readonly agGridModules = clientSideAgGridModules;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly shipmentService: ShipmentsService,
		private readonly batchesService: BatchesService,
		private readonly commercialDocumentService: DocumentService,
		private readonly translationService: TranslationService
	) {
		this.shipmentId = <string> this.route.snapshot.params['id'];

		this.isOffline = !navigator.onLine;
		this.isLoading = true;
		this.isDocumentLoading = true;
		this.user = this.userService.user;
		this.overlayNoRowsTemplate = this.translationService.getTR('order-card.no-rows-to-show');
		this.overlayNoDocumentsTemplate = this.translationService.getTR('order.no-documents');
		this.invoiceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True');
		this.certificateDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True');
		this.dispatchAdviceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'DispatchAdvice', 'True');
		this.orderAckDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True');
		this.shipmentSub = this.getObservableForCard().pipe(
			switchMap(([shipment, batches]) => {
				if (Object.keys(shipment).length === 0) {
					this.isLoading = false;
					this.isShipmentNotFound = true;
					return of({ shipment, batches, documentResults: undefined });
				}

				this.shipment = shipment;
				this.batches = batches;
				this.constructShipmentBatchesGrid();
				this.isLoading = false;

				if (batches.results.length > 0
					&& (this.invoiceDisplayed || this.dispatchAdviceDisplayed
						|| this.certificateDisplayed || this.orderAckDisplayed)) {
					return this.commercialDocumentService.getDocuments(
						this.documentsFields,
						this.constructDocumentFilter(batches.results),
						0,
						100,
						new OrderBy(SORT_BY, SORT_WAY),
						false
					).pipe(
						map(documentResults => ({ documentResults })),
						catchError((err: unknown) => {
							console.error('error loading docs', err);
							this.isDocumentLoading = false;
							return of({ documentResults: undefined });
						})
					);
				}
				this.documentBoxDisplayed = false;
				return of({ documentResults: undefined });
			})
		).subscribe({
			next: ({ documentResults }) => {
				this.documentResults = documentResults;
				if (documentResults) {
					this.constructCommercialDocuments();
				}
				this.isDocumentLoading = false;
			},
			error: (err: unknown) => {
				console.error('error loading', err);
				this.isLoading = false;
			}
		});
	}

	get shipmentBatchCount(): number | null {
		const count = this.batches?.results.length ?? 0;
		return count > 0 ? count : null;
	}

	get shipmentCommercialDocCount(): number | null {
		const count = this.documentResults?.results.length ?? 0;
		return count > 0 ? count : null;
	}

	ngOnDestroy() {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
		this.shipmentSub.unsubscribe();
		if (this.documentSub !== undefined) {
			this.documentSub.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent) {
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	distinctConcatValues(value: string | undefined): string {
		if (!value) {
			return '';
		}
		if (!value.includes(';')) {
			return value;
		}
		return [...new Set(value.split(';'))].join('; ');
	}

	private getObservableForCard() {
		return this.shipmentService.getShipmentCard(this.shipmentId).pipe(
			map((shipment: ShipmentCard) => {
				this.shipment = shipment;
				return shipment;
			}),

			switchMap((shipment: ShipmentCard) => {
				if (!shipment.batches || shipment.batches.length === 0) {
					const empty: PaginatedResult<Batch> = {
						results: [],
						count: 0,
						page: 1,
						limit: 0
					};
					this.batches = empty;
					return of<[ShipmentCard, PaginatedResult<Batch>]>([shipment, empty]);
				}

				let filterExpr: NodeExpression | LuceneExpression | undefined;
				for (const batch of shipment.batches) {
					const batchRefExpr: FieldExpression = {
						field: 'id',
						term: batch.supplierBatchReference,
						quoted: true
					};
					filterExpr = filterExpr
						? {
							left: filterExpr,
							operator: 'OR',
							parenthesized: false,
							right: batchRefExpr
						}
						: batchRefExpr;
				}

				const filter = filterExpr ? luceneToString(filterExpr) : '';

				return this.batchesService.getDatacenterBatchesV5(
					[
						'id',
						'batchReference',
						'supplierOrderNumber',
						'supplierOrderItemNumber',
						'status',
						'statusDescription',
						'currentLocationDescription',
						'form',
						'formDescription',
						'thickness',
						'width',
						'length',
						'qtyBatchNet',
						'qtyBatchGross',
						'producedLength',
						'readyToShipDate',
						'supplierBatchReference',
						'loadingProgramNumber',
						'carrierName'
					],
					filter,
					0,
					250,
					[new OrderBy('batchReference', OrderByDirection.ASC)],
					false
				).pipe(
					map((batches: PaginatedResult<Batch>): [ShipmentCard, PaginatedResult<Batch>] =>
						[shipment, batches])
				);
			})
		);
	}

	private createDaInOrderFieldExpression(batch: Batch) {
		const supplierOrderReferenceItemField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReferenceItem`,
			term: batch.supplierOrderItemNumber,
			quoted: true
		};
		const supplierOrderReferenceFieldFromDocLine: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReference`,
			term: padSupplierOrderReference(batch.supplierOrderNumber),
			quoted: true
		};
		const supplierOrderNumberNode: NodeExpression = {
			left: supplierOrderReferenceFieldFromDocLine,
			operator: 'AND',
			right: supplierOrderReferenceItemField,
			parenthesized: true
		};

		return supplierOrderNumberNode;
	}

	private createOACeOrderFieldExpression(batch: Batch) {
		const supplierOrderReferenceItemField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReferenceItem`,
			term: batch.supplierOrderItemNumber,
			quoted: true
		};
		const supplierOrderReferenceFieldFromDoc: FieldExpression = {
			field: 'supplierOrderReference',
			term: padSupplierOrderReference(batch.supplierOrderNumber),
			quoted: true
		};
		const supplierOrderNumberNode: NodeExpression = {
			left: supplierOrderReferenceFieldFromDoc,
			operator: 'AND',
			right: supplierOrderReferenceItemField,
			parenthesized: true
		};

		return supplierOrderNumberNode;
	}

	private createBatchFieldExpression(batch: Batch) {
		const batchReferenceItemField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.batchNumber`,
			term: batch.batchReference,
			quoted: true
		};
		return batchReferenceItemField;
	}

	private constructDocumentFilter(
		batches: Batch[] | undefined
	) {
		if (!batches) {
			return '';
		}
		const typeOAFieldFromDoc: FieldExpression = {
			field: 'type',
			term: DOCUMENT_TYPE_ORDER_ACKNOWLEDEGEMENT,
			quoted: true
		};
		const typeCEFieldFromDoc: FieldExpression = {
			field: 'type',
			term: DOCUMENT_TYPE_CERTIFICATE,
			quoted: true
		};
		const typeDAFieldFromDoc: FieldExpression = {
			field: 'type',
			term: DOCUMENT_TYPE_DISPATCH_ADVICE,
			quoted: true
		};
		const typeINFieldFromDoc: FieldExpression = {
			field: 'type',
			term: DOCUMENT_TYPE_INVOICE,
			quoted: true
		};

		let orderOaCeResult: NodeExpression | LuceneExpression | undefined;
		for (const batch of batches) {
			orderOaCeResult = orderOaCeResult
				? {
					left: orderOaCeResult,
					operator: 'OR',
					parenthesized: true,
					right: this.createOACeOrderFieldExpression(batch)
				}
				: this.createOACeOrderFieldExpression(batch);
		}

		let oafinalNode: NodeExpression | LuceneExpression | undefined;
		if (orderOaCeResult) {
			oafinalNode = {
				left: orderOaCeResult,
				operator: 'AND',
				right: typeOAFieldFromDoc,
				parenthesized: true
			};
		}

		let batchResult: NodeExpression | LuceneExpression | undefined;
		for (const batch of batches) {
			batchResult = batchResult
				? {
					left: batchResult,
					operator: 'OR',
					parenthesized: true,
					right: this.createBatchFieldExpression(batch)
				}
				: this.createBatchFieldExpression(batch);
		}
		let ceOrderBatchNode: NodeExpression | LuceneExpression | undefined;
		if (orderOaCeResult && batchResult) {
			ceOrderBatchNode = {
				left: orderOaCeResult,
				operator: 'AND',
				right: batchResult,
				parenthesized: true
			};
		}
		let cefinalNode: NodeExpression | LuceneExpression | undefined;
		if (ceOrderBatchNode) {
			cefinalNode = {
				left: ceOrderBatchNode,
				operator: 'AND',
				right: typeCEFieldFromDoc,
				parenthesized: true
			};
		}

		let orderDaInResult: NodeExpression | LuceneExpression | undefined;
		for (const batch of batches) {
			orderDaInResult = orderDaInResult
				? {
					left: orderDaInResult,
					operator: 'OR',
					parenthesized: true,
					right: this.createDaInOrderFieldExpression(batch)
				}
				: this.createDaInOrderFieldExpression(batch);
		}

		let daInOrderBatchNode: NodeExpression | LuceneExpression | undefined;
		if (orderDaInResult && batchResult) {
			daInOrderBatchNode = {
				left: orderDaInResult,
				operator: 'AND',
				right: batchResult,
				parenthesized: true
			};
		}
		let dafinalNode: NodeExpression | LuceneExpression | undefined;
		if (daInOrderBatchNode) {
			dafinalNode = {
				left: daInOrderBatchNode,
				operator: 'AND',
				right: typeDAFieldFromDoc,
				parenthesized: true
			};
		}

		let inFinalNode: NodeExpression | LuceneExpression | undefined;
		if (daInOrderBatchNode) {
			inFinalNode = {
				left: daInOrderBatchNode,
				operator: 'AND',
				right: typeINFieldFromDoc,
				parenthesized: true
			};
		}

		let finalNode: NodeExpression | LuceneExpression | undefined;
		if (this.orderAckDisplayed) {
			finalNode = oafinalNode;
		}
		if (cefinalNode && this.certificateDisplayed) {
			finalNode = finalNode ? {
				left: finalNode,
				operator: 'OR',
				right: cefinalNode,
				parenthesized: true
			} : cefinalNode;
		}
		if (dafinalNode && this.dispatchAdviceDisplayed) {
			finalNode = finalNode ? {
				left: finalNode,
				operator: 'OR',
				right: dafinalNode,
				parenthesized: true
			} : dafinalNode;
		}
		if (inFinalNode && this.invoiceDisplayed) {
			finalNode = finalNode ? {
				left: finalNode,
				operator: 'OR',
				right: inFinalNode,
				parenthesized: true
			} : inFinalNode;
		}

		return finalNode ? luceneToString(finalNode) : undefined;
	}

	private constructShipmentBatchesGrid() {
		if (!this.batches) {
			return;
		}
		this.shipmentBatchesOptions = new CommonGridOptions<Batch>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: this.batches.results,
			domLayout: 'autoHeight',
			columnDefs: [
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('order.supplier.order.number'),
					field: 'supplierOrderNumber',
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						return params.data.supplierOrderNumber && params.data.supplierOrderItemNumber
							? `${ params.data.supplierOrderNumber }/${ params.data.supplierOrderItemNumber }`
							: params.data.supplierOrderNumber || params.data.supplierOrderItemNumber;
					},
					cellRendererParams: {
						routerLink: (params: ICellRendererParams<Batch, unknown>) => {
							if (!params.data?.supplierOrderNumber || !params.data.supplierOrderItemNumber) {
								return undefined;
							}

							return {
								commands: [
									'/order-tracking',
									'order',
									'card',
									params.data.supplierOrderNumber,
									params.data.supplierOrderItemNumber,
								],
							};
						}
					}
				}),
				new LinkColDef<Batch>({
					headerName: this.translationService.getTR('batch.reference'),
					field: 'batchReference',
					cellRendererParams: linkToBatchCardCellRendererParams
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('batch.detailed-batch-status'),
					field: 'statusDescription'
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('batch.current-location'),
					field: 'currentLocationDescription'
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('order.form'),
					field: 'formDescription',
					valueGetter: params => {
						const formDesc = params.data?.formDescription || '';
						const thickness = params.data?.thickness || '';
						const width = params.data?.width || '';
						const length = params.data?.length ?? '';
						return formDesc === 'sheet'
							? `${ formDesc } \nDim: ${ thickness } x ${ width } x ${ length }`
							: `${ formDesc } \nDim: ${ thickness } x ${ width }`;
					},
					cellStyle: {
						whiteSpace: 'pre-line',
						overflow: 'visible',
					},
					autoHeight: true,
				}),
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('batch.net-t'),
					field: 'qtyBatchNet',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new NumericColDef<Batch>({
					headerName: this.translationService.getTR('batch.gross-t'),
					field: 'qtyBatchGross',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.producedLength'),
					field: 'producedLength',
					valueGetter: params => {
						if (!params.data) {
							return undefined;
						}
						if (params.data.form === '04' && params.data.producedLength) {
							return Number(params.data.producedLength) / 1000;
						}
						return undefined;
					},
					cellRendererParams: { numericFormat: 'number' },
					cellStyle: { textAlign: 'center' }
				}),
				new DateColDef<Batch>({
					headerName: this.translationService.getTR('batch.date-for-dispatch'),
					field: 'readyToShipDate'
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.loading-program-number'),
					field: 'loadingProgramNumber'
				}),
				new CommonColDef<Batch>({
					headerName: this.translationService.getTR('datacenter-filter.carrier-name'),
					field: 'carrierName'
				}),
			]
		});
	}

	private constructCommercialDocuments() {
		if (this.documentResults) {
			for (const document of this.documentResults.results) {
				const globalDocument: GlobalDocument = {
					title: document.type === 'IN' ? this.translationService.getTR('order.card.document.invoices')
						: document.type === 'DA' ? this.translationService.getTR('order.card.document.dispatch.advices')
							: document.type === 'CE' ? this.translationService.getTR('order.card.document.certificates')
								: this.translationService.getTR('order.card.document.order.acknowledgments'),
					name: document.name,
					date: document.date,
					id: document.id,
					subtype: document.subtypeDescription,
					type: document.type,
					proforma: document.proforma
				};

				this.globalDocuments.push(globalDocument);
			}
		}
		this.constructGridCommercialDocumentsOptions(this.globalDocuments);
	}

	private constructGridCommercialDocumentsOptions(globalDocuments: GlobalDocument[]) {
		this.gridCommercialDocumentsOptions = new CommonGridOptions<GlobalDocument>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			rowData: globalDocuments,

			columnDefs: [
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.document'),
					field: 'title',
				}),
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.type'),
					field: 'subtype',
				}),
				...globalDocuments.some(d => d.proforma === 'true') ? [
					new CommonColDef<GlobalDocument>({
						headerName: this.translationService.getTR('order-card.proforma'),
						field: 'proforma',
						sortable: true,
						valueFormatter: params => {
							if (params.value === 'true') {
								return this.translationService.getTR('global.yes');
							}
							if (params.value === 'false') {
								return this.translationService.getTR('global.no');
							}
							return '';
						}
					})
				] : [],
				new LinkColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.name'),
					field: 'name',
					cellRendererParams: linkToDocumentCellRendererParams
				}),
				new DateColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order.creation.date'),
					field: 'date',
					width: 100,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' }
				}),
				new IconRedirectColDef<GlobalDocument>({
					field: 'name',
					cellRendererParams: {
						linkData: (params: ICellRendererParams<Document, unknown>) => {
							const { data } = params;
							if (!data) {
								return undefined;
							}
							return {
								url: this.commercialDocumentService
									.getDownloadDocumentUrl(data.id, data.name),
								type: 'application/pdf',
								icon: 'table_document'
							};
						}
					}
				})
			]
		});
	}
}
